/**
 * Quick Logger Component
 * Compact two-line interface for standup notes and time logging
 * Intelligently switches between standup and hours logging modes
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Task, User, StandupNote } from '../types';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Clock, Send, AlertCircle, CheckCircle } from 'lucide-react';
import { sortByTaskOrder } from '../utils/taskFiltering';
import { APP_FIELD_THEME_CLASS, APP_NATIVE_FIELD_CLASS } from '../constants/formControls';
import { usePreferences } from '../contexts/PreferencesContext';

interface QuickLoggerProps {
  currentUser: User;
  assignedTasks: Task[];
  onStandupSubmit: (note: StandupNote) => Promise<void>;
  onLogHours: (taskId: string, hours: number, description: string, date: string) => Promise<void>;
}

type LogMode = 'standup' | 'hours';

export const QuickLogger: React.FC<QuickLoggerProps> = ({
  currentUser,
  assignedTasks,
  onStandupSubmit,
  onLogHours,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [mode, setMode] = useState<LogMode>('standup');
  const [text, setText] = useState('');
  const [selectedTask, setSelectedTask] = useState<string>('');
  const [hours, setHours] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const textInputRef = useRef<HTMLTextAreaElement>(null);
  const { getTranslation } = usePreferences();

  // Focus on expand
  useEffect(() => {
    if (isExpanded && textInputRef.current) {
      textInputRef.current.focus();
    }
  }, [isExpanded]);

  const availableTasks = useMemo(() => {
    return sortByTaskOrder(assignedTasks || []);
  }, [assignedTasks]);

  const selectedTaskDetails = useMemo(
    () => availableTasks.find(t => t.id === selectedTask),
    [selectedTask, availableTasks]
  );

  const isValid = useMemo(() => {
    if (mode === 'standup') {
      return text.trim().length > 0;
    } else {
      return selectedTask && hours && parseFloat(hours) > 0;
    }
  }, [mode, text, selectedTask, hours]);

  const handleSubmit = async () => {
    if (!isValid) return;

    setLoading(true);
    try {
      if (mode === 'standup') {
        const note: StandupNote = {
          id: `standup-${Date.now()}`,
          organizationId: currentUser.organizationId,
          authorId: currentUser.id,
          authorName: currentUser.name,
          content: text,
          createdAt: new Date().toISOString(),
          taskId: selectedTask || undefined,
          taskTitle: selectedTaskDetails?.title,
          projectId: selectedTaskDetails?.projectId,
          source: 'manual'
        };
        await onStandupSubmit(note);
      } else {
        await onLogHours(
          selectedTask,
          parseFloat(hours),
          text,
          new Date().toISOString().split('T')[0]
        );
      }

      // Reset
      setText('');
      setSelectedTask('');
      setHours('');
      setIsExpanded(false);
      setMessage({
        type: 'success',
        text: mode === 'standup' ? 'Standup posted!' : 'Hours logged!'
      });
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.message || 'Failed to submit'
      });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleModeChange = (newMode: LogMode) => {
    setMode(newMode);
    setText('');
    setSelectedTask('');
    setHours('');
  };

  return (
    <div className="flex flex-col gap-2">
      {/* Status Message */}
      {message && (
        <div className={`px-3 py-2 rounded text-sm flex items-center gap-2 ${
          message.type === 'success'
            ? 'border border-chart-2/40 bg-chart-2/10 text-chart-2'
            : 'border border-destructive/40 bg-destructive/10 text-destructive'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="w-4 h-4" />
          ) : (
            <AlertCircle className="w-4 h-4" />
          )}
          {message.text}
        </div>
      )}

      <div className={`flex flex-col overflow-hidden rounded-lg border border-border bg-card text-card-foreground transition-all ${
        isExpanded ? 'border-primary ring-1 ring-primary/40 shadow-md' : ''
      }`}>
        {/* Line 1: Expandable Input */}
        <div className="flex flex-col border-border p-3">
          <textarea
            ref={textInputRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onFocus={() => setIsExpanded(true)}
            placeholder={
              mode === 'standup'
                ? getTranslation('whatDidYouAccomplishToday', 'What did you accomplish today?')
                : 'Describe your work...'
            }
            className="w-full resize-none border-none bg-transparent p-0 text-sm text-foreground outline-none placeholder:text-muted-foreground"
            rows={isExpanded ? 3 : 1}
          />
        </div>

        {/* Line 2: Controls (Task Selector + Mode + Hours + Submit) */}
        {isExpanded && (
          <div className="grid grid-cols-5 items-center gap-2 bg-muted/40 p-3 md:grid-cols-5 sm:grid-cols-4 max-sm:grid-cols-3">
            {/* Task Selector (Always visible when expanded) */}
            <Select value={selectedTask} onValueChange={setSelectedTask}>
              <SelectTrigger className={`h-9 text-sm ${APP_FIELD_THEME_CLASS}`}>
                <SelectValue placeholder="Select task..." />
              </SelectTrigger>
              <SelectContent>
                {availableTasks.length === 0 ? (
                  <div className="p-2 text-sm text-muted-foreground">No assigned tasks</div>
                ) : (
                  availableTasks.map((task) => (
                    <SelectItem key={task.id} value={task.id}>
                      {task.title}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>

            {/* Mode Selector */}
            <Select value={mode} onValueChange={(v) => handleModeChange(v as LogMode)}>
              <SelectTrigger className={`h-9 min-w-24 text-sm ${APP_FIELD_THEME_CLASS}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="standup">
                  <span className="flex items-center gap-2">
                    📝 {getTranslation('dailyStandup', 'Standup')}
                  </span>
                </SelectItem>
                <SelectItem value="hours">
                  <span className="flex items-center gap-2">
                    <Clock className="w-4 h-4" /> {getTranslation('logHours', 'Log Hours')}
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>

            {/* Hours Input (Visible only in hours mode) */}
            {mode === 'hours' && (
              <input
                type="number"
                min="0.25"
                step="0.25"
                max="24"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                placeholder="Hours"
                className={`${APP_NATIVE_FIELD_CLASS} h-9 text-center text-sm`}
              />
            )}

            {/* Submit Button */}
            <Button
              onClick={handleSubmit}
              disabled={!isValid || loading}
              size="sm"
              className="h-9 text-sm whitespace-nowrap"
            >
              <Send className="w-4 h-4" />
              {loading ? 'Saving...' : 'Submit'}
            </Button>

            {/* Close Button */}
            <Button
              variant="ghost"
              onClick={() => {
                setIsExpanded(false);
                setText('');
                setSelectedTask('');
                setHours('');
              }}
              size="sm"
              className="h-9 w-9 p-0 text-lg text-muted-foreground hover:text-foreground"
            >
              ✕
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickLogger;
